import clsx from 'clsx'
import { Helmet } from "react-helmet"
import React, { useEffect, useMemo, useState } from "react"
import { MenuButton, MenuItem } from "@headlessui/react"
import { Link, useLocation, useNavigate } from "react-router-dom"

import { USER } from '../../api/Registry'
import { ErrorState } from '../errors/ErrorState'
import { COOKIE_KEYS } from '../../utils/Constants'
import DropDown from '../../lib/fragments/DropDown'
import HttpServices from '../../services/HttpServices'
import { LoadingXL } from '../../components/utils/LoadingXL'
import { usePageContext } from '../../lib/guards/PageContext'
import { readFromCookie, DateFormating, generateUniqueId, persistedPageSize } from '../../utils/Helpers'
import { PageSizeSelector } from '../../lib/fragments/PageSizeSelector'
import { ModuleBreadCrumb } from '../../lib/fragments/ModuleBreadCrumb'
import { G_onInputBlurHandler, G_onInputChangeHandler } from '../../components/utils/InputHandlers'
import { InputBasic } from '../../components/utils/InputBasic'
import { Checkbox } from '../../components/utils/Checkbox'
import ReactTable from '../../lib/fragments/ReactTable'
import { CreateManagedAccount } from './CreateManagedAccount'

export const ManagedAccounts = () => {
    const [state, setstate] = useState({
        posting: false,
        loading: true,
        httpStatus: 200,
        status: 'pending',
        data: {
            manifest: [],
            links: [],
            meta: {
                to: 0,
                from: 0,
                total: 0,
                lastPage: null,
                currentPage: null,
                lastPageUrl: null,
            }
        },
        input: {
            search: '',
            account: 'Y',
            auth: '',
            last: '',
            created: '',
            status: '',
        },
        errors: {
            search: ''
        },
        show: {
            uuid: null,
            modal: '',
        }
    })

    const [pageSize, setPageSize] = useState(() =>
        persistedPageSize()
    );

    const [displayColumns, setDisplayColumns] = useState([
        { id: '0', key: 'e', label: 'Account', default: true },
        { id: '1', key: 'a', label: 'Auth', default: false },
        { id: '2', key: 'd', label: 'Last Active', default: false },
        { id: '3', key: 'c', label: 'Created On', default: false },
        { id: '4', key: 'n', label: 'Status', default: false },
        { id: '5', key: 'z', label: '', default: true },
    ])

    const location = useLocation()
    const navigate = useNavigate()
    const pageTitle = "Managed Accounts"
    const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

    type ModalKeys = 'createManagedAccount'
    const { updateDataContext } = usePageContext();

    const breadCrumb = [
        { linkItem: false, title: 'IAM' },
        { linkItem: false, title: pageTitle },
    ]

    useEffect(() => {
        dataContextHandler()
        setVisibleColumns(displayColumns.map((c) => c.key));

        fetchManagedAccounts(pageSize)
    }, [])

    const fetchManagedAccounts = async (pageSize: number, navigateTo?: string | null) => {
        let { status, httpStatus, data } = state

        try {
            const routeParams = new URLSearchParams({ records: String(pageSize) });
            const baseUrl = navigateTo ?? USER.MANAGED_ACCOUNTS;
            const fullUrl = `${baseUrl}?${routeParams}`;

            const response: any = await HttpServices.httpGet(fullUrl);
            httpStatus = response.status

            if (httpStatus === 200) {
                const dataBlock = response.data

                data.manifest = dataBlock.data
                data.links = dataBlock.links

                data.meta.to = dataBlock.to
                data.meta.from = dataBlock.from
                data.meta.total = dataBlock.total
                data.meta.lastPage = dataBlock.last_page
                data.meta.currentPage = dataBlock.current_page
                data.meta.lastPageUrl = dataBlock.last_page_url

                status = 'fulfilled'
                dataContextHandler('fulfilled')
            } else {
                status = 'rejected'
            }
        } catch (error) {
            status = 'rejected'
            httpStatus = 500
        }

        setstate((prevState: any) => ({
            ...prevState,
            status,
            httpStatus,
            loading: false,
            data: {
                ...prevState.data,
            },
        }))
    }

    const dataContextHandler = (status: string = 'pending') => {
        const baseContext = {
            title: pageTitle,
        };

        if (status !== 'fulfilled') {
            setTimeout(() => {
                updateDataContext(baseContext);
            }, 500)

            return;
        }

        updateDataContext({
            ...baseContext,
            mainAction: {
                visible: true,
                disabled: false,
                icon: 'far fa-plus',
                label: 'Create New User',
                onClick: () => toggleModal('createManagedAccount', true),
                variant: 'primary',
            },
        });
    }

    const columnarLedger = useMemo(
        () => [
            {
                key: 'e',
                Header: 'Account',
                id: generateUniqueId('Account'),
                accessor: (user: { e: any, t: any }) => {
                    return (
                        <div className="text-slate-700 dark:text-slate-200 md:block truncate text-ellipsis overflow-hidden whitespace-nowrap">
                            {user.e}

                            <span className="block text-slate-500 dark:text-slate-400">
                                {user.t}
                            </span>
                        </div>
                    )
                }
            },
            {
                key: 'a',
                Header: 'Auth',
                id: generateUniqueId('Auth'),
                accessor: (user: { a: any }) => (
                    <div className="hidden md:flex align-middle items-center gap-2 w-full text-slate-500 dark:text-slate-400 pt-1 min-w-0 truncate text-ellipsis overflow-hidden whitespace-nowrap">
                        <span className={clsx(
                            user.a === 'Email/Password' && 'fa-light fa-envelope',
                            user.a === 'Google Workspace' && 'fa-brands fa-google',
                            "fa-lg",
                        )}></span>

                        <span className="md:block hidden">
                            {user.a}
                        </span>
                    </div>
                ),
            },
            {
                key: 'd',
                Header: 'Last Active',
                id: generateUniqueId('Last Active'),
                accessor: (user: { d: any }) => {
                    function lastLoggedInFunc() {
                        const lastLoggedIn = DateFormating(user.d, 'DD MMM YYYY, hh:mm A [UTC]')
                        const [lastDate, lastTime] = lastLoggedIn.split(",");

                        return <>
                            {lastDate} <span className="md:hidden lg:inline-block">
                                {lastTime}
                            </span>
                        </>
                    }

                    return (
                        (
                            <div className="hidden md:flex flex-wrap align-middle items-center w-full text-slate-500 dark:text-slate-400 pt-1 min-w-0">
                                <span className="w-full sm:flex-1 text-slate-500 dark:text-slate-400 truncate text-ellipsis overflow-hidden whitespace-nowrap">
                                    {
                                        user.d ? (
                                            lastLoggedInFunc()
                                        ) : (
                                            'Never logged in'
                                        )
                                    }
                                </span>
                            </div>
                        )
                    )
                },
            },
            {
                key: 'c',
                Header: 'Created On',
                id: generateUniqueId('Created On'),
                accessor: (user: { c: any }) => {
                    const createdDate = DateFormating(user.c, 'Do MMM YYYY')

                    return (
                        <div className="hidden lg:flex flex-wrap align-middle items-center gap-4 w-full text-slate-500 dark:text-slate-400 pt-1 min-w-0">
                            <span className="w-full sm:flex-1 text-slate-500 dark:text-slate-400 truncate text-ellipsis overflow-hidden whitespace-nowrap">
                                {createdDate}
                            </span>
                        </div>
                    )
                }
            },
            {
                key: 'n',
                Header: 'Status',
                id: generateUniqueId('Status'),
                accessor: (user: { n: any }) => (
                    <div className="hidden lg:flex flex-wrap align-middle items-center gap-4 w-full text-slate-500 dark:text-slate-400 pt-1 min-w-0">
                        <span className={clsx(
                            user.n === 'Active' && 'bg-green-100 dark:bg-green-800/40 text-green-600 dark:text-green-400',
                            user.n === 'LOCKED' && 'bg-amber-100 dark:bg-amber-800/40 text-amber-500 dark:text-amber-400',
                            user.n === 'DISABLED' && 'bg-red-100 dark:bg-red-800/40 text-red-500 dark:text-red-400',
                            user.n === 'INACTIVE' && 'bg-slate-100 dark:bg-slate-800/40 text-slate-500 dark:text-slate-400',
                            "rounded-md px-1.5 py-1 text-xs ring-0 ring-inset"
                        )}>
                            {user.n}
                        </span>
                    </div>
                )
            },
            {
                key: 'z',
                Header: ' ',
                id: generateUniqueId('-'),
                accessor: (data: { uuid: any }) => (
                    <div className="lg:flex flex flex-row-reverse align-middle items-center gap-x-2 w-full text-slate-500 dark:text-slate-400 pt-1 min-w-0 md:opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <div className="flex-none">
                            <DropDown
                                type={'borderless'}
                                items={
                                    <div className="w-40">
                                        <MenuItem>
                                            <button type="button" /* onClick={() => navigateToEditUserAttribute(attr)} */ className="w-full rounded-md px-3 py-2 text-sm data-[focus]:bg-slate-200 dark:data-[focus]:bg-slate-700 dark:data-[focus]:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 text-start">
                                                Edit Attribute
                                            </button>
                                        </MenuItem>

                                        <div className="pt-1 mb-1 w-[90%] mx-auto border-b border-slate-300 dark:border-slate-600"></div>

                                        <MenuItem>
                                            <button type="button" /* onClick={() => handleProviderCatalog(catalog)} */
                                                className="w-full rounded-md px-3 text-red-700 dark:text-red-600 py-2 text-sm data-[focus]:bg-red-200 dark:data-[focus]:bg-red-900 data-[focus]:text-red-900 dark:data-[focus]:text-white hover:bg-red-200 dark:hover:bg-red-800 text-start">
                                                Disable
                                            </button>
                                        </MenuItem>
                                    </div>
                                }
                            />
                        </div>

                        <button type="button" className="">
                            <span className="fa-light fa-file-edit fa-lg"></span>
                        </button>
                    </div>
                ),
            },
        ], []
    )

    const reorderedDisplayColumns = async (newDisplayList: any[]) => {
        let { loading } = state
        if (loading) return

        setDisplayColumns(newDisplayList);

        setVisibleColumns(prevVisible => {
            // Extract keys from the new order
            const orderedKeys = newDisplayList.map(c => c.key);

            // Keep only keys that were already visible
            const filteredAndOrdered = orderedKeys.filter(key => prevVisible.includes(key));

            // Return the reordered, valid list
            return filteredAndOrdered;
        });

        console.log('Reordered →', newDisplayList);
    }

    const onPageSizeChangeHandler = (pageSize: any) => {
        let { loading } = state
        if (loading) return

        setstate((prevState: any) => ({
            ...prevState,
            loading: true,
        }));

        fetchManagedAccounts(pageSize)
    }

    const pageNavigationHandler = (navigateTo: string) => {
        let { loading } = state
        if (loading) return

        setstate((prevState: any) => ({
            ...prevState,
            loading: true,
        }));

        fetchManagedAccounts(pageSize, navigateTo)
    }

    const toggleModal = (key: ModalKeys, value: boolean, uuid?: any) => {
        setstate((prevState: any) => ({
            ...prevState,
            show: {
                ...prevState.show,
                modal: value ? key : '',
                uuid: uuid,
            },
        }))
    }








    const onPageSizeChange = (pageSize: any) => {
        if (!pageSize) return

        setstate((prev: any) => ({
            ...prev,
            loading: true,
        }))

        fetchManagedAccounts(pageSize)
    }

    const onChangeHandler = (e: any) => {
        let { posting } = state

        if (!posting) {
            let { input } = state
            let { errors } = state
            let output: any = G_onInputChangeHandler(e)

            const updatedInput = {
                ...input,
                [e.target.name]: output.value
            };

            const updatedErrors = {
                ...errors,
                [e.target.name]: output.error
            };

            setstate((prev) => ({
                ...prev,
                input: updatedInput,
                errors: updatedErrors,
            }));
        }
    }

    const onInputBlur = (e: any) => {
        let { posting } = state

        if (!posting) {
            let { input } = state
            let { errors } = state
            let output: any = G_onInputBlurHandler(e, '')

            const updatedInput = {
                ...input,
                [e.target.name]: output.value
            };

            const updatedErrors = {
                ...errors,
                [e.target.name]: output.error
            };

            setstate((prev) => ({
                ...prev,
                input: updatedInput,
                errors: updatedErrors,
            }));
        }
    }

    const toggleColumn = (displayColumn: any) => {
        const key = displayColumn?.key
        if (displayColumn.default) return;

        setVisibleColumns((prev) => {
            let updated: string[];

            if (prev.includes(key)) {
                // remove key
                updated = prev.filter((col) => col !== key);
            } else {
                // add but enforce max 6 (inclusive of z)
                if (prev.length >= 6) {
                    return prev; // no change
                }

                updated = [...prev, key];
            }

            // always put z last if it exists
            if (updated.includes("z")) {
                updated = updated.filter((col) => col !== "z");
                updated.push("z");
            }

            return updated;
        });
    };

    return (
        <React.Fragment>
            <Helmet>
                <title>{pageTitle}</title>
            </Helmet>

            <div className="flex flex-col gap-x-3 w-full mx-auto h-full">
                <div className="w-full py-2 border- border-slate-200 dark:border-slate-600 lg:px-8 md:px-8 px-5 flex flex-col">
                    <ModuleBreadCrumb breadCrumb={breadCrumb} />
                </div>

                {
                    state.status === 'rejected' ? (
                        <div className="py-2 px-6 w-full mx-auto flex-1 flex flex-col justify-center">
                            <ErrorState
                                code={state.httpStatus}
                            />
                        </div>
                    ) : state.status === 'fulfilled' ? (
                        <div className="md:h-full lg:h-full flex flex-col">
                            <div className="header-title align-middle title-transition lg:px-6 px-5 pt-3">
                                <div className="md:hidden flex flex-col pb-3 gap-y-3">
                                    <div className="flex-1">
                                        <h3 className="text-slate-900 dark:text-white text-xl md:font-normal font-medium tracking-wide">
                                            {pageTitle}
                                        </h3>

                                        <p className="text-slate-600 dark:text-slate-300 md:pt-0 lg:pt-2 pb-4 text-sm md:w-[512px] md:hidden pt-4">
                                            Accounts created and maintained by your organization. Typically assigned to employees or in-house staff
                                        </p>
                                    </div>

                                    <div className="flex-none">
                                        <button type="button" onClick={() => toggleModal('createManagedAccount', true)} className={`w-auto min-w-24 flex align-middle items-center gap-x-2 px-3 py-2 text-sm font-medium text-orange-600 dark:text-orange-400 border border-orange-600 dark:border-orange-400 rounded-lg bg-white dark:bg-slate-900 hover:bg-orange-50 dark:hover:bg-orange-900/30 focus:bg-orange-100 dark:focus:bg-orange-900/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ease-in-out`}>
                                            <span className="fa-doutone fa-plus fa-xl"></span>

                                            <span className="text-sm">
                                                Create Account
                                            </span>
                                        </button>
                                    </div>
                                </div>

                                <p className="text-slate-600 dark:text-slate-300 md:pt-2 lg:pt-2 pb-4 text-sm md:w-[512px] hidden md:block md:px-2">
                                    Accounts created and maintained by your organization. Typically assigned to employees or in-house staff
                                </p>
                            </div>

                            <div className="w-full pb-7 lg:px-6 md:px-6 px-5">
                                <ReactTable
                                    columns={columnarLedger}
                                    manifest={state.data.manifest}
                                    loading={state.loading}
                                    pageHandler={onPageSizeChangeHandler}
                                    meta={state.data.meta}
                                    links={state.data.links}
                                    navigation={pageNavigationHandler}
                                    input={state.input}
                                    showPageSize={true}
                                    visibleColumns={visibleColumns}
                                    toggleColumn={toggleColumn}
                                    display={displayColumns}
                                    reorderfunc={reorderedDisplayColumns}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col justify-center w-full overflow-auto py-4">
                            <LoadingXL />
                        </div>
                    )
                }

                <CreateManagedAccount
                    show={state.show.modal === 'createManagedAccount' ? true : false}
                    showOrHide={() => toggleModal('createManagedAccount', false)}
                    onComplete={undefined}
                />
            </div>
        </React.Fragment>
    )
}